/* eslint-disable @typescript-eslint/no-explicit-any */
import { createFileRoute } from "@tanstack/react-router";
import { useAuth, useVenueDetailsBySlug, useI18N } from "common/hooks";
import { useEffect, useState } from "react";
import { useGeoLocation } from "@/hooks/useGeoLocation";
import { defaultRegion } from "common/config";
import Loader from "@/components/common/Loader";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import { Helmet } from "react-helmet";
import { Storage } from "common/storage";
import AnalyticsService from "@/services/analytics";
import ComplexVenueTabsContent from "../components/VenueDetailComplex/ComplexVenueTabsContent";
import { useTrackPageView } from "@/hooks/useTrackPageView";
import { getVenueSlugs, getStoredPasswordToken, getStoredSsoToken } from "@/lib/utils";

export type ComplexVenueTabsRouteParams = {
  venueSlug: string; // from the route path"/complexvenuetabs/$venueSlug/$tabId"
  tabId: string; // the business tab ID
};

export type ComplexVenueTabsRouteSearch = {
  source?: "map" | "activity";
  showAuthModal?: boolean;
  cid?: string;
  tid?: string;
};

export const Route = createFileRoute("/complexvenuetabs/$venueSlug/$tabId")({
  component: ComplexVenueTabs,
  validateSearch: (
    search: Record<string, unknown>,
  ): ComplexVenueTabsRouteSearch => {
    return {
      source: search.source as "map" | "activity",
      showAuthModal: search.showAuthModal as boolean,
      cid: search.cid as string,
      tid: search.tid as string,
    };
  },
  parseParams: (params: Record<string, string>) => {
    return {
      venueSlug: params.venueSlug,
      tabId: params.tabId,
    };
  },
});

function ComplexVenueTabs() {
  const { venueSlug, tabId } = Route.useParams();
  const { showAuthModal, cid, tid } = Route.useSearch();

  const { user, isLoading, isFetching } = useAuth();
  const isAuthLoading = isLoading || isFetching;
  const i18n = useI18N();

  const [showAuthModalState, setShowAuthModalState] = useState(showAuthModal);
  const { location } = useGeoLocation();

  // load this venue from the server
  const {
    data,
    error,
    refetch: loadVenueDetails,
    isLoading: isDetailsLoading,
  } = useVenueDetailsBySlug({
    slug: venueSlug,
    latitude:
      location?.latitude.toString() || defaultRegion.latitude.toString(),
    longitude:
      location?.longitude.toString() || defaultRegion.longitude.toString(),
    accessToken: Storage.getCurrentUserToken(),
    enabled: location ? true : false,
    type: "Complex",
    passwordToken: getStoredPasswordToken(venueSlug),
    ssoToken: getStoredSsoToken(venueSlug),
  });

  const { showModal } = useAuthModals();

  useEffect(() => {
    if (location && !isAuthLoading) {
      loadVenueDetails();
    }
  }, [location, loadVenueDetails, user, isAuthLoading]);

  useEffect(() => {
    if (showAuthModalState && !user && !isAuthLoading) {
      showModal(AuthModalScreen.EnterEmail);
      setShowAuthModalState(false);
    }
  }, [showAuthModalState, user, isAuthLoading, showModal]);

  // Page load tracking
  useTrackPageView("complexVenueTabsPageViewed", {
    ready: !!data?.details,
    data: {
      VenueId: data.details.business.id,
      VenueName: data.details.name,
      VenueType: data.details.businessType,
      TemplateType: "Complex",
      Source: "complex_venue_tabs_page",
      TabId: tabId,
      CampaignId: cid,
      TrackingId: tid,
    },
  });

  // Find the specific business tab
  const currentTab = data?.details?.businessTabs?.find(
    (tab) => tab.id.toString() === tabId,
  );

  return (
    <main id="main" className="bg-bg-brand-secondary-default">
      <Helmet>
        <title>
          {(() => {
            const venueName = data?.details?.name
              ? data.details.name.length > 30
                ? data.details.name.substring(0, 30) + "..."
                : data.details.name
              : "";

            if (currentTab) {
              return `${currentTab.title} | ${venueName} | Sociability`;
            }
            return `${venueName} | Complex Venue Tabs | Sociability`;
          })()}
        </title>
      </Helmet>
      {(isAuthLoading || isDetailsLoading) && (
        <>
          <div role="status" aria-live="polite" className="sr-only">
            {i18n.get("loadingVenueInformation")}
          </div>
          <Loader />
        </>
      )}
      <section className="mb-2" aria-label={i18n.get("venueContent")}>
        {error && (
          <div className="max-w-[1160px] px-4 mx-auto overflow-hidden" role="alert">
            <div>{error.message}</div>
          </div>
        )}

        {data && currentTab && (
          <ComplexVenueTabsContent
            venueDetailsResponse={data}
            businessTab={currentTab}
            onClose={() => {
              const { venueDetailsSlug } = getVenueSlugs(data.details);
              window.location.href = `/venuedetails/${venueDetailsSlug}`;
            }}
            onRefresh={() => {
              setTimeout(() => loadVenueDetails(), 1000);
            }}
            cid={cid}
            tid={tid}
          />
        )}

        {data && !currentTab && (
          <div className="max-w-[1160px] px-4 mx-auto overflow-hidden" role="alert">
            <div className="text-center py-8">
              <h1 className="label-m-semibold text-gray-900 mb-2">
                {i18n.get("contentNotFound")}
              </h1>
              <p className="text-gray-600 mb-4">
                {i18n.get("requestedContentNotFound")}
              </p>
              <button
                onClick={() => {
                  // Track analytics
                  AnalyticsService.trackGAEventHandler("complexVenueTabContentNotFoundBackClicked");
                  AnalyticsService.trackMx("complexVenueTabContentNotFoundBackClicked", {
                    VenueId: data.details.business.id,
                    VenueName: data.details.name,
                    TabId: tabId,
                  });
                  
                  const { venueDetailsSlug } = getVenueSlugs(data.details);
                  window.location.href = `/venuedetails/${venueDetailsSlug}`;
                }}
                className="bg-bg-brand-primary-default hover:bg-bg-brand-primary-hover text-white px-4 py-2 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
              >
                {i18n.get("backToVenue")}
              </button>
            </div>
          </div>
        )}
      </section>
    </main>
  );
}
